<template>
  <div>
    <Breadcrumbs main="Settings" title="Settings" />

    <div class="container-fluid">
      <div class="row widget-grid m-lg-5">
        <div class="card">
          <div class="card-body">
            <div class="body-title">
              Withdrawals
            </div>
            <div class="row mt-5">
              <!-- Withdrawal Fee 區塊 -->
              <div class="col-xxl-3 col-12 mt-2 position-relative">
                <span class="label-title">Withdrawal fee</span>
                <div class="content">
                  ${{ withdrawalFee }}
                  <button
                    @click="togglePopover('withdrawal_fee', $event)"
                    class="btn btn-link text-primary btn-edit ms-1"
                    ref="editWithdrawFeeLink"
                  >
                    Edit fee
                  </button>
                </div>
                <div
                  v-if="editing === 'withdrawal_fee'"
                  class="popover bs-popover-bottom show"
                  role="tooltip"
                >
                  <div class="popover-body">
                    <div class="p-3">
                      <div class="box-title">Change withdrawal fee</div>
                      <input
                        type="text"
                        class="form-control mb-3 green-border"
                        v-model="newWithdrawalFee"
                      />
                      <div class="d-flex justify-content-end">
                        <button class="btn btn-link text-secondary" @click="cancelEdit">Cancel</button>
                        <button class="btn btn-link text-primary btn-ok" @click="saveWithdrawalFee">OK</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Payer Name 區塊 -->
              <div class="col-xxl-3 col-12 mt-2 position-relative">
                <span class="label-title">Payer name</span>
                <div class="content">
                  {{ payerName }}
                  <button
                    @click="togglePopover('payer_name', $event)"
                    class="btn btn-link text-primary btn-edit ms-1"
                    ref="editPayerNameLink"
                  >
                    Edit name
                  </button>
                </div>
                <div
                  v-if="editing === 'payer_name'"
                  class="popover bs-popover-bottom show"
                  role="tooltip"
                >
                  <div class="popover-body">
                    <div class="p-3">
                      <div class="box-title">Change payer name</div>
                      <input
                        type="text"
                        class="form-control mb-3 green-border"
                        v-model="newPayerName"
                      />
                      <div class="d-flex justify-content-end">
                        <button class="btn btn-link text-secondary" @click="cancelEdit">Cancel</button>
                        <button class="btn btn-link text-primary btn-ok" @click="savePayerName">OK</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-body">
            <div class="body-title">
              Pre-payment cards
            </div>
            <div class="row mt-5">
              <!-- Monthly Fee 區塊 -->
              <div class="col-xxl-3 col-12 mt-2 position-relative">
                <span class="label-title">Monthly fee</span>
                <div class="content">
                  ${{ monthlyFee }}
                  <button
                    @click="togglePopover('monthly_fee', $event)"
                    class="btn btn-link text-primary btn-edit ms-1"
                    ref="editMonthlyFeeLink">
                    Edit monthly fee
                  </button>
                </div>
                <div
                  v-if="editing === 'monthly_fee'"
                  class="popover bs-popover-bottom show"
                  role="tooltip"
                >
                  <div class="popover-body">
                    <div class="p-3">
                      <div class="box-title">Change monthly fee</div>
                      <input
                        type="text"
                        class="form-control mb-3 green-border"
                        v-model="newMonthlyFee"
                        placeholder="Enter amount"
                      />
                      <div class="d-flex justify-content-end">
                        <button class="btn btn-link text-secondary" @click="cancelEdit">Cancel</button>
                        <button class="btn btn-link text-primary btn-ok" @click="saveMonthlyFee">OK</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Monthly Charge Date 區塊 -->
              <div class="col-xxl-3 col-12 mt-2 position-relative">
                <span class="label-title">Monthly charge date</span>
                <div class="content">
                  {{ formatChargeDate(monthlyChargeDate) }}
                  <button
                    @click="togglePopover('monthly_charge_date', $event)"
                    class="btn btn-link text-primary btn-edit ms-1"
                    ref="editChargeDateLink"
                  >
                    Edit date
                  </button>
                </div>
                <div
                  v-if="editing === 'monthly_charge_date'"
                  class="popover bs-popover-bottom show date-picker-popover"
                  role="tooltip"
                >
                  <div class="popover-body">
                    <div class="p-3">
                      <div class="box-title">Change monthly charge date</div>
                      <div class="date-picker-grid">
                        <button
                          v-for="day in daysInMonth"
                          :key="day"
                          class="date-day-btn"
                          :class="{ active: selectedDay === day }"
                          @click="selectDay(day)"
                        >
                          {{ day }}
                        </button>
                      </div>
                      <div class="d-flex justify-content-end mt-3">
                        <button class="btn btn-link text-secondary" @click="cancelEdit">Cancel</button>
                        <button class="btn btn-link text-primary btn-ok" @click="saveMonthlyChargeDate">OK</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, computed } from 'vue';
import { useSettingsStore } from '../../store/admin/settings.';

const settingsStore = useSettingsStore();

// 使用新變數名稱來對應 withdrawal_fee 與 payer_name
const editing = ref('');
const newWithdrawalFee = ref('');
const newPayerName = ref('');
const newMonthlyFee = ref('');
const selectedDay = ref(null);
const newMonthlyChargeDate = ref(null);

const withdrawalFee = computed(() => {
  if (settingsStore.settings && Array.isArray(settingsStore.settings)) {
    const feeSetting = settingsStore.settings.find(s => s.meta_key === 'withdrawal_fee');
    return feeSetting ? feeSetting.meta_value : '';
  }
  return '';
});

const payerName = computed(() => {
  if (settingsStore.settings && Array.isArray(settingsStore.settings)) {
    const payerSetting = settingsStore.settings.find(s => s.meta_key === 'payer_name');
    return payerSetting ? payerSetting.meta_value : '';
  }
  return '';
});

const monthlyFee = computed(() => {
  if (settingsStore.settings && Array.isArray(settingsStore.settings)) {
    const feeSetting = settingsStore.settings.find(s => s.meta_key === 'monthly_fee');
    return feeSetting ? feeSetting.meta_value : '5.00';
  }
  return '5.00';
});

const monthlyChargeDate = computed(() => {
  if (settingsStore.settings && Array.isArray(settingsStore.settings)) {
    const dateSetting = settingsStore.settings.find(s => s.meta_key === 'monthly_charge_date');
    return dateSetting ? dateSetting.meta_value : '15';
  }
  return '15';
});

// 生成 1-31 的日期陣列
const daysInMonth = computed(() => {
  return Array.from({ length: 31 }, (_, i) => i + 1);
});

// 格式化日期顯示（例如：15th）
const formatChargeDate = (date) => {
  if (!date) return '15th';
  const day = parseInt(date);
  if (isNaN(day)) return date;
  const suffix = day === 1 || day === 21 || day === 31 ? 'st' :
                 day === 2 || day === 22 ? 'nd' :
                 day === 3 || day === 23 ? 'rd' : 'th';
  return `${day}${suffix}`;
};

const togglePopover = (type, event) => {
  if (editing.value === type) {
    cancelEdit();
  } else {
    showPopover(type, event);
  }
};

const showPopover = (type, event) => {
  const target = event.target.closest('button') || event.target;
  const parentContainer = target.closest('.position-relative');
  
  if (type === 'withdrawal_fee') {
    editing.value = 'withdrawal_fee';
    newWithdrawalFee.value = withdrawalFee.value;
    nextTick(() => {
      if (parentContainer) {
        const popover = parentContainer.querySelector('.popover');
        if (popover && target) {
          const buttonRect = target.getBoundingClientRect();
          const containerRect = parentContainer.getBoundingClientRect();
          // 使用相對定位，相對於 parentContainer
          popover.style.top = `${buttonRect.bottom - containerRect.top + 5}px`;
          popover.style.left = `${buttonRect.left - containerRect.left}px`;
        }
      }
    });
  } else if (type === 'payer_name') {
    editing.value = 'payer_name';
    newPayerName.value = payerName.value;
    nextTick(() => {
      if (parentContainer) {
        const popover = parentContainer.querySelector('.popover');
        if (popover && target) {
          const buttonRect = target.getBoundingClientRect();
          const containerRect = parentContainer.getBoundingClientRect();
          // 使用相對定位，相對於 parentContainer
          popover.style.top = `${buttonRect.bottom - containerRect.top + 5}px`;
          popover.style.left = `${buttonRect.left - containerRect.left}px`;
        }
      }
    });
  } else if (type === 'monthly_fee') {
    editing.value = 'monthly_fee';
    newMonthlyFee.value = monthlyFee.value;
    nextTick(() => {
      if (parentContainer) {
        const popover = parentContainer.querySelector('.popover');
        if (popover && target) {
          const buttonRect = target.getBoundingClientRect();
          const containerRect = parentContainer.getBoundingClientRect();
          // 使用相對定位，相對於 parentContainer
          popover.style.top = `${buttonRect.bottom - containerRect.top + 5}px`;
          popover.style.left = `${buttonRect.left - containerRect.left}px`;
        }
      }
    });
  } else if (type === 'monthly_charge_date') {
    editing.value = 'monthly_charge_date';
    selectedDay.value = parseInt(monthlyChargeDate.value) || 15;
    nextTick(() => {
      if (parentContainer) {
        const popover = parentContainer.querySelector('.popover');
        if (popover && target) {
          const buttonRect = target.getBoundingClientRect();
          const containerRect = parentContainer.getBoundingClientRect();
          // 電腦版：顯示在按鈕右側，往上調整
          popover.style.top = `${buttonRect.top - containerRect.top - 100}px`;
          popover.style.left = `${buttonRect.right - containerRect.left + 10}px`;
        }
      }
    });
  }
};

const cancelEdit = () => {
  editing.value = '';
};

const saveWithdrawalFee = async () => {
  const feeSetting = settingsStore.settings.find(s => s.meta_key === 'withdrawal_fee');
  const payload = {
    id: feeSetting ? feeSetting.id : null,
    meta_key: 'withdrawal_fee',
    meta_value: newWithdrawalFee.value
  };
  await settingsStore.updateSettings(payload);
  editing.value = '';
};

const savePayerName = async () => {
  const payerSetting = settingsStore.settings.find(s => s.meta_key === 'payer_name');
  const payload = {
    id: payerSetting ? payerSetting.id : null,
    meta_key: 'payer_name',
    meta_value: newPayerName.value
  };
  await settingsStore.updateSettings(payload);
  editing.value = '';
};

const saveMonthlyFee = async () => {
  const feeSetting = settingsStore.settings.find(s => s.meta_key === 'monthly_fee');
  const payload = {
    id: feeSetting ? feeSetting.id : null,
    meta_key: 'monthly_fee',
    meta_value: newMonthlyFee.value
  };
  await settingsStore.updateSettings(payload);
  editing.value = '';
};

const selectDay = (day) => {
  selectedDay.value = day;
  newMonthlyChargeDate.value = day.toString();
};

const saveMonthlyChargeDate = async () => {
  const dateSetting = settingsStore.settings.find(s => s.meta_key === 'monthly_charge_date');
  const payload = {
    id: dateSetting ? dateSetting.id : null,
    meta_key: 'monthly_charge_date',
    meta_value: newMonthlyChargeDate.value || selectedDay.value.toString()
  };
  await settingsStore.updateSettings(payload);
  editing.value = '';
};

onMounted(async () => {
  await settingsStore.fetchSettings();
});
</script>

<style scoped>
.body-title {
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 30px;
  display: flex;
  align-items: center;
  color: #051C2C;
}

.text-primary {
  color: rgba(82, 72, 218, 1) !important;
}

.text-primary:hover {
  color: rgba(82, 72, 218, 1) !important;
}

.text-secondary {
  color: #666 !important;
  font-family: Euclid Square;
  font-size: 18px;
  font-weight: 500;
  line-height: 22.82px;
  text-align: right;
}

.position-relative {
  position: relative;
}

.label-title {
  font-family: Mulish;
  font-size: 18px;
  font-weight: 400;
  line-height: 22.59px;
  text-align: left;
}

.box-title {
  font-family: Mulish;
  font-size: 18px;
  font-weight: 400;
  line-height: 22.59px;
  text-align: left;
  margin-bottom: 10px;
}

.content {
  font-family: Euclid Square;
  font-size: 42px;
  font-weight: 400;
  line-height: 53.26px;
  text-align: left;
}

.popover {
  max-width: 400px;
  position: absolute;
  z-index: 1000;
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.popover-body {
  padding: 1.5rem;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.green-border {
  border: 2px solid #00c962 !important;
  font-family: Euclid Square;
  font-size: 18.36px;
  font-weight: 400;
  line-height: 23.28px;
  text-align: left;
}

.btn-edit {
  font-family: Euclid Square;
  font-size: 16px;
  font-weight: 400;
  line-height: 20.29px;
  text-align: left;
}

.btn-ok {
  font-family: Euclid Square;
  font-size: 18px;
  font-weight: 500;
  line-height: 22.82px;
  text-align: right;
}

.popover .btn-link {
  color: rgba(82, 72, 218, 1);
  text-decoration: none;
}

.popover .btn-link:hover {
  color: rgba(82, 72, 218, 1);
  text-decoration: underline;
}

.popover-arrow {
  display: none;
}

.date-picker-popover {
  min-width: 300px;
}

.date-picker-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  margin-bottom: 10px;
}

.date-day-btn {
  width: 100%;
  aspect-ratio: 1;
  border: 1px solid #e0e0e0;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  color: #333;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.date-day-btn:hover {
  background-color: #f5f5f5;
  border-color: #00c962;
}

.date-day-btn.active {
  background-color: #00c962;
  color: white;
  border-color: #00c962;
  font-weight: 500;
}
</style>
